import {
  CustomColumnDefinitionDtoMetadataType,
  type PartnerStructuredColumnDto,
} from '@artic/api-client';

import {
  buildProductIndex,
  isDirectBindProduct,
  productContainerWrites,
  productLookupFieldFor,
  productTargetLabel,
  productTypeOptionIdOf,
  resolveProduct,
  type ImportProduct,
  type ProductIndex,
  type ProductLookupField,
  type ProductResolution,
} from '../../imports/product-resolution';
import {
  applyTransform,
  PRODUCT_CODE_TARGET,
  PRODUCT_ID_FIELD,
  PRODUCT_NAME_TARGET,
  PROJECT_CODE_TARGET,
  PROJECT_NAME_TARGET,
  type ImportMapping,
  type ImportMappingEntry,
  type ImportTransform,
  type ParsedRow,
} from '../../imports/transform';
import type { PartnerContainer, PartnerContainerOption } from '../../products/types';
import { optionAppliesToProductType } from '../cell-applicability';
import { canEditField, type EditabilityContext, type EditabilityRow } from '../cell-editability';
import { formatNumberWithUnit, parseNumberWithUnitPaste } from '../metadata-cell-editors';
import type { ProjectStatus, WorkOrderRow } from '../types';
import { IDENTITY_FIELD, type ImportTarget } from './import-catalog';

export type { ImportMapping, ImportMappingEntry, ParsedRow } from '../../imports/transform';

export type PreviewRowStatus = 'update' | 'new' | 'invalid';

/** A resolved cell write, addressed exactly as the batch-update payload expects. */
export interface ResolvedEdit {
  field: string;
  value: string | null;
}

/** One cell in the preview grid (display + diff), for a single mapped target. */
export interface PreviewCell {
  field: string;
  label: string;
  newDisplay: string;
  /** Current value display for an existing (update) row; null for a new row. */
  oldDisplay: string | null;
  changed: boolean;
  /** Existing row's status forbids this field — it will be skipped (partial update). */
  locked: boolean;
}

export interface PreviewRow {
  /** 0-based index into the parsed data rows. */
  sourceIndex: number;
  identity: string;
  /**
   * Resolved `project.code` / `project.name` for the row (advisory preview
   * only). The server resolves the target project per-row from these; the
   * client just surfaces what it sees. `null` when no `project.*` is mapped.
   */
  projectCode: string | null;
  projectName: string | null;
  status: PreviewRowStatus;
  /** Set when status === 'update'. */
  workOrderId: string | null;
  /** Set when status === 'new' — the Product Type the row will be created with. */
  productTypeOptionId: string | null;
  /**
   * Set when the row resolved to a Product carrying no container selections
   * (a legacy product): there are no container options to expand, so the work
   * order binds `product_id` directly instead of being derived from a Product
   * Type. Mutually exclusive with `productTypeOptionId` on a create row.
   */
  productId: string | null;
  /** Non-empty ⇒ the row is invalid and excluded from import. */
  errors: string[];
  /** Writes to apply (excludes locked cells and the create-managed Product Type). */
  edits: ResolvedEdit[];
  /** Per-target display rows for the preview grid. */
  cells: PreviewCell[];
  /** update: any non-locked cell changes; new: always true (whole row is written). */
  hasChanges: boolean;
}

export interface ImportValidationInput {
  rows: ParsedRow[];
  mapping: ImportMapping;
  catalog: ImportTarget[];
  workOrders: WorkOrderRow[];
  containers: PartnerContainer[];
  structuredColumns: PartnerStructuredColumnDto[];
  legacyWorkOrderIds: ReadonlySet<string>;
  projectStatus: ProjectStatus;
  /**
   * The partner's products, for a mapping that names a Product / Product Code
   * instead of spelling out container options. Omit (or pass `[]`) when the
   * mapping carries no `product.*` target — every such row then errors as
   * unmatched, which is the correct outcome for an unresolvable product.
   */
  products?: readonly ImportProduct[];
}

export interface ImportValidationCounts {
  update: number;
  create: number;
  invalid: number;
  unchanged: number;
}

export interface ImportValidationResult {
  previews: PreviewRow[];
  counts: ImportValidationCounts;
}

const MT = CustomColumnDefinitionDtoMetadataType;

/**
 * Resolve every parsed Excel row against the current work orders + partner
 * config: decide update vs create vs invalid, validate container options and
 * typed values, compute the per-cell changes, and produce the exact edits to
 * apply. Pure — no I/O — so it is cheap to re-run on every mapping change and
 * easy to unit test.
 */
export function validateImport(input: ImportValidationInput): ImportValidationResult {
  const {
    rows,
    mapping,
    catalog,
    workOrders,
    containers,
    structuredColumns,
    legacyWorkOrderIds,
    projectStatus,
    products = [],
  } = input;

  const catalogByField = new Map(catalog.map((t) => [t.field, t]));
  const ctx: EditabilityContext = {
    containers,
    structuredColumns,
    legacyWorkOrderIds,
    projectStatus,
  };

  // Existing work orders keyed by name — used for matching + ambiguity detection.
  // Archived work orders are skipped: a re-import of an archived work order's
  // name must fall through to "new" (creating a fresh work order), never match
  // or resurrect the archived row, and must not count toward the "matches more
  // than one" ambiguity check. `work_order_current_status === 'archived'` is the
  // canonical archived signal — the status trigger sets it whenever the work
  // order's `is_archived` is true. The grid feeds archived rows into this
  // preview when the "Show archived" toggle is on, so the exclusion matters here.
  const byName = new Map<string, WorkOrderRow[]>();
  for (const wo of workOrders) {
    if (wo.work_order_current_status === 'archived') continue;
    const key = wo.work_order_name;
    const list = byName.get(key);
    if (list) list.push(wo);
    else byName.set(key, [wo]);
  }

  // option_code → container_option_id for the Product Type container.
  const productTypeContainer = containers.find((c) => c.container_key === 'product_type');
  const ptCodeToOptionId = new Map(
    (productTypeContainer?.options ?? []).map((o) => [o.option_code, o.container_option_id]),
  );

  // The identity column's transform (the match key). Dedupe active entries by
  // target so a (UI-prevented) double-map resolves deterministically. The
  // `project.*` targets are consumed by the server's project resolver, not
  // here — surfaced in the preview only (advisory).
  const identityTransform =
    mapping.find((m) => m.targetField === IDENTITY_FIELD)?.transform ?? null;
  const projectCodeTransform =
    mapping.find((m) => m.targetField === PROJECT_CODE_TARGET)?.transform ?? null;
  const projectNameTransform =
    mapping.find((m) => m.targetField === PROJECT_NAME_TARGET)?.transform ?? null;

  // A mapped Product / Product Code supersedes container columns for the whole
  // file: the resolved product's own selections define them. The editor
  // disables (and strips) container rows while one is set, but a mapping stored
  // before that — or hand-edited — could still carry them, so they are ignored
  // here too, keeping the sheet's product column the single source of truth.
  //
  // The two product targets are mutually exclusive (the editor enforces it and
  // the backend rejects both on write), but a mapping persisted before that
  // guard could still carry both. Prefer Product Code explicitly rather than
  // letting array order decide — matching the server.
  const productEntry =
    mapping.find((m) => m.targetField === PRODUCT_CODE_TARGET) ??
    mapping.find((m) => m.targetField === PRODUCT_NAME_TARGET) ??
    null;
  const productLookupField = productEntry ? productLookupFieldFor(productEntry.targetField) : null;
  const productIndex = productEntry ? buildProductIndex(products) : null;

  const activeEntries = new Map<string, ImportMappingEntry>();
  for (const entry of mapping) {
    if (
      !entry.targetField ||
      entry.targetField === IDENTITY_FIELD ||
      entry.targetField === PROJECT_CODE_TARGET ||
      entry.targetField === PROJECT_NAME_TARGET ||
      productLookupFieldFor(entry.targetField) != null
    ) {
      continue;
    }
    if (!catalogByField.has(entry.targetField)) continue;
    if (productEntry && catalogByField.get(entry.targetField)!.kind === 'container') continue;
    if (!activeEntries.has(entry.targetField)) activeEntries.set(entry.targetField, entry);
  }

  // Work Order names that appear on more than one row of this sheet. Creating
  // them would mint duplicate-named work orders that a later re-import can no
  // longer resolve ("Matches N existing work orders — cannot resolve"), so every
  // row carrying a duplicated name is rejected up front. The name is taken from
  // the resolved identity transform, so this covers both a plain mapped column
  // and a calculated transform. Blank identities are handled by the per-row
  // "Missing Work Order identifier" check, not counted here.
  const duplicateIdentities = findDuplicateIdentities(rows, identityTransform);

  const previews = rows.map((row, sourceIndex) =>
    validateRow({
      row,
      sourceIndex,
      identityTransform,
      duplicateIdentities,
      projectCodeTransform,
      projectNameTransform,
      activeEntries,
      catalogByField,
      byName,
      productTypeContainer,
      ptCodeToOptionId,
      productEntry,
      productLookupField,
      productIndex,
      ctx,
    }),
  );

  const counts: ImportValidationCounts = { update: 0, create: 0, invalid: 0, unchanged: 0 };
  for (const p of previews) {
    if (p.status === 'invalid') counts.invalid += 1;
    else if (p.status === 'new') counts.create += 1;
    else if (p.hasChanges) counts.update += 1;
    else counts.unchanged += 1;
  }

  return { previews, counts };
}

/**
 * The set of non-blank identities that occur on more than one row, resolved
 * through the identity transform so plain-column and calculated-transform
 * mappings are both covered. Blank identities are excluded — those are reported
 * per-row as a missing identifier, not as a duplicate.
 */
function findDuplicateIdentities(
  rows: ParsedRow[],
  identityTransform: ImportTransform | null,
): Set<string> {
  if (identityTransform == null) return new Set();
  const counts = new Map<string, number>();
  for (const row of rows) {
    const identity = applyTransform(identityTransform, row);
    if (identity === '') continue;
    counts.set(identity, (counts.get(identity) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [identity, count] of counts) {
    if (count > 1) duplicates.add(identity);
  }
  return duplicates;
}

interface ValidateRowArgs {
  row: ParsedRow;
  sourceIndex: number;
  identityTransform: ImportTransform | null;
  /** Non-blank identities that occur on more than one row of the sheet. */
  duplicateIdentities: ReadonlySet<string>;
  projectCodeTransform: ImportTransform | null;
  projectNameTransform: ImportTransform | null;
  activeEntries: Map<string, ImportMappingEntry>;
  catalogByField: Map<string, ImportTarget>;
  byName: Map<string, WorkOrderRow[]>;
  productTypeContainer: PartnerContainer | undefined;
  ptCodeToOptionId: Map<string, string>;
  /** The mapped `product.*` entry, or null when the file names no product. */
  productEntry: ImportMappingEntry | null;
  productLookupField: ProductLookupField | null;
  productIndex: ProductIndex | null;
  ctx: EditabilityContext;
}

/** A single mapped column resolved to its edits + display for one row. */
interface ResolvedField {
  field: string;
  label: string;
  edits: ResolvedEdit[];
  newDisplay: string;
  error?: string;
}

function validateRow(args: ValidateRowArgs): PreviewRow {
  const {
    row,
    sourceIndex,
    identityTransform,
    duplicateIdentities,
    projectCodeTransform,
    projectNameTransform,
    activeEntries,
    catalogByField,
    byName,
    ctx,
  } = args;

  const errors: string[] = [];
  const identity = identityTransform == null ? '' : applyTransform(identityTransform, row);
  const projectCode = projectCodeTransform ? applyTransform(projectCodeTransform, row) : null;
  const projectName = projectNameTransform ? applyTransform(projectNameTransform, row) : null;

  if (identityTransform == null) errors.push('No Work Order column is mapped.');
  else if (identity === '') errors.push('Missing Work Order identifier.');
  else if (duplicateIdentities.has(identity)) {
    errors.push(
      `Duplicate Work Order name "${identity}" — it appears on more than one row of the sheet.`,
    );
  }

  const matches = identity === '' ? [] : (byName.get(identity) ?? []);
  let status: PreviewRowStatus = matches.length === 1 ? 'update' : 'new';
  const existingRow = matches.length === 1 ? matches[0]! : null;
  if (matches.length > 1) {
    errors.push(`Matches ${matches.length} existing work orders — cannot resolve.`);
  }

  // Resolve the effective Product Type first: with a Product / Product Code
  // mapped the resolved product supplies it, otherwise a mapped Product Type
  // value wins and (update) the existing row's current one is the fallback. New
  // rows require one to be created — unless they bind a product directly.
  const productResolution = resolveRowProduct(args, row);
  if (productResolution.error) errors.push(productResolution.error);
  const product = productResolution.product;
  const directBind = product != null && isDirectBindProduct(product);

  let effectivePtOptionId: string | null;
  if (args.productEntry) {
    // A blank product cell leaves an existing row's product alone; on a new row
    // there is nothing to create from.
    effectivePtOptionId = product
      ? productTypeOptionIdOf(product)
      : existingProductTypeOptionId(existingRow, args.ptCodeToOptionId);
    if (status === 'new' && !product && !productResolution.error) {
      errors.push(
        `${productTargetLabel(args.productLookupField!)} is required to create a new work order.`,
      );
    }
    if (status === 'new' && product && !directBind && !effectivePtOptionId) {
      errors.push(
        `Product "${product.product_name}" has no Product Type option — cannot create a work order from it.`,
      );
    }
  } else {
    const ptResolution = resolveProductType(args, row, existingRow);
    if (ptResolution.error) errors.push(ptResolution.error);
    effectivePtOptionId = ptResolution.optionId;
    if (status === 'new' && !effectivePtOptionId && !ptResolution.error) {
      errors.push('A valid Product Type is required to create a new work order.');
    }
  }

  const resolvedFields: ResolvedField[] = [];
  for (const entry of activeEntries.values()) {
    const target = catalogByField.get(entry.targetField)!;
    const resolved = resolveField({
      target,
      entry,
      raw: applyTransform(entry.transform, row),
      status,
      effectivePtOptionId,
      ptOptionId: target.container?.container_key === 'product_type' ? effectivePtOptionId : null,
    });
    resolvedFields.push(resolved);
    if (resolved.error) errors.push(resolved.error);
  }
  // The resolved product's own selections stand in for the container columns.
  if (product) resolvedFields.push(...resolveProductFields(args, product, status));

  // Any error ⇒ the whole row is skipped (never partially imported).
  if (errors.length > 0) {
    return {
      sourceIndex,
      identity,
      projectCode,
      projectName,
      status: 'invalid',
      workOrderId: existingRow?.work_order_id ?? null,
      productTypeOptionId: null,
      productId: null,
      errors,
      edits: [],
      cells: buildInvalidCells(resolvedFields, existingRow, catalogByField),
      hasChanges: false,
    };
  }

  const cells: PreviewCell[] = [];
  const edits: ResolvedEdit[] = [];
  let hasChanges = status === 'new';

  // Identity cell: for a new row we write the name; for an update it's the match key.
  cells.push({
    field: IDENTITY_FIELD,
    label: 'Work Order',
    newDisplay: identity,
    oldDisplay: existingRow ? existingRow.work_order_name : null,
    changed: false,
    locked: false,
  });
  if (status === 'new') edits.push({ field: IDENTITY_FIELD, value: identity });

  // A new row is created as `draft`, so check editability against a draft row
  // rather than skipping the check — a column that isn't editable on draft
  // (e.g. one editable only later in the lifecycle) must not enter the create
  // payload, or the backend rejects the whole batch.
  const editabilityRow: EditabilityRow = existingRow ?? {
    work_order_id: '',
    work_order_current_status: 'draft',
  };

  for (const rf of resolvedFields) {
    const locked = !canEditField(rf.field, editabilityRow, ctx);
    const oldDisplay = existingRow ? currentDisplay(rf.field, existingRow, catalogByField) : null;
    const changed = existingRow ? oldDisplay !== rf.newDisplay : true;
    cells.push({
      field: rf.field,
      label: rf.label,
      newDisplay: rf.newDisplay,
      oldDisplay,
      changed,
      locked,
    });
    if (locked) continue;
    edits.push(...rf.edits);
    if (existingRow && changed) hasChanges = true;
  }

  // A new row with no product type edit still creates via batchAdd; an update
  // with no effective change is a no-op we'll let the UI default to skipping.
  return {
    sourceIndex,
    identity,
    projectCode,
    projectName,
    status,
    workOrderId: existingRow?.work_order_id ?? null,
    // A direct-bind create carries the product itself instead of a Product
    // Type — the create path binds `product_id` and skips find-or-create.
    productTypeOptionId: status === 'new' && !directBind ? effectivePtOptionId : null,
    productId: status === 'new' && directBind ? product!.product_id : null,
    errors: [],
    edits,
    cells,
    hasChanges,
  };
}

/**
 * Resolve the row's Product / Product Code cell, when one is mapped. A blank
 * cell is not an error here — the caller decides whether it is fatal (creating
 * a row) or a no-op (leaving an existing row's product alone).
 */
function resolveRowProduct(args: ValidateRowArgs, row: ParsedRow): ProductResolution {
  const { productEntry, productLookupField, productIndex } = args;
  if (!productEntry || !productLookupField || !productIndex) return { product: null };
  return resolveProduct(
    productIndex,
    productLookupField,
    applyTransform(productEntry.transform, row),
  );
}

/**
 * Expand a resolved product into the container writes the row applies —
 * byte-identical to what a fully-mapped container import would have produced,
 * so nothing downstream can tell the two apart.
 *
 * Every partner container is written, not just the selected ones: a container
 * the product does NOT select is cleared, exactly as a blank container cell
 * would clear it. Without that an existing row would keep stale selections and
 * the planner's find-or-create would rebind it to a *different* product than
 * the sheet named.
 *
 * A product with no selections at all is the exception — it is bound by
 * `product_id` directly (there is nothing to expand), and emitting clears would
 * rebind the row onto the empty-selection placeholder product instead.
 *
 * Product Type applicability is deliberately not re-validated: these selections
 * came off a real product, so they were consistent when it was created, and a
 * since-changed scoping rule is pruned at write time like any container edit.
 */
function resolveProductFields(
  args: ValidateRowArgs,
  product: ImportProduct,
  status: PreviewRowStatus,
): ResolvedField[] {
  const { catalogByField } = args;

  if (isDirectBindProduct(product)) {
    // On a create the bind happens at add time (see PreviewRow.productId); on
    // an update it is the row's only write.
    if (status === 'new') return [];
    return [
      {
        field: PRODUCT_ID_FIELD,
        label: 'Product',
        edits: [{ field: PRODUCT_ID_FIELD, value: product.product_id }],
        newDisplay: product.product_name || product.product_key,
      },
    ];
  }

  const codeByContainerKey = new Map(
    productContainerWrites(product).map((w) => [w.field, w.value] as const),
  );
  const fields: ResolvedField[] = [];

  for (const target of catalogByField.values()) {
    if (target.kind !== 'container') continue;
    const container = target.container!;
    const displayOf = (code: string | null): string => {
      if (!code) return '';
      return container.options.find((o) => o.option_code === code)?.option_name ?? code;
    };

    if (container.container_key === 'product_type') {
      // Applied by the create path on a new row; a settable (draft) existing
      // row is rebound through the normal container edit.
      const ptCode =
        product.selections.find((s) => s.container_key === 'product_type')?.option_code ?? null;
      if (status === 'new' || !ptCode) continue;
      fields.push({
        field: target.field,
        label: target.label,
        edits: [{ field: target.field, value: ptCode }],
        newDisplay: displayOf(ptCode),
      });
      continue;
    }

    const code = codeByContainerKey.get(container.container_key) ?? null;
    fields.push({
      field: target.field,
      label: target.label,
      edits: [{ field: target.field, value: code }],
      newDisplay: displayOf(code),
    });
  }

  return fields;
}

/** The existing row's Product Type option id, or null when it has none. */
function existingProductTypeOptionId(
  existingRow: WorkOrderRow | null,
  ptCodeToOptionId: Map<string, string>,
): string | null {
  const code = existingRow ? asCode(existingRow['product_type']) : '';
  return code ? (ptCodeToOptionId.get(code) ?? null) : null;
}

interface ProductTypeResolution {
  optionId: string | null;
  error?: string;
}

/** Resolve the row's effective Product Type option (mapped value wins, else existing). */
function resolveProductType(
  args: ValidateRowArgs,
  row: ParsedRow,
  existingRow: WorkOrderRow | null,
): ProductTypeResolution {
  const { productTypeContainer, ptCodeToOptionId } = args;
  if (!productTypeContainer) {
    // No Product Type container configured — fall back to the existing row's.
    return { optionId: existingProductTypeOptionId(existingRow, ptCodeToOptionId) };
  }

  // A mapped Product Type only takes effect where it can be written — a new row,
  // or an existing draft row. On non-draft rows Product Type is locked, so other
  // containers' applicability must be judged against the *existing* Product Type.
  const ptSettable = existingRow == null || existingRow.work_order_current_status === 'draft';
  const entry = args.activeEntries.get('product_type');
  if (ptSettable && entry) {
    const raw = applyTransform(entry.transform, row);
    if (!isBlank(raw)) {
      const option = resolveContainerOption(productTypeContainer, cellString(raw));
      if (!option) {
        return {
          optionId: null,
          error: `Product Type: "${cellString(raw)}" is not a valid option.`,
        };
      }
      return { optionId: option.container_option_id };
    }
  }

  // Not mapped / blank / not settable → use the existing row's Product Type.
  return { optionId: existingProductTypeOptionId(existingRow, ptCodeToOptionId) };
}

interface ResolveFieldArgs {
  target: ImportTarget;
  entry: ImportMappingEntry;
  raw: unknown;
  status: PreviewRowStatus;
  effectivePtOptionId: string | null;
  ptOptionId: string | null;
}

function resolveField(args: ResolveFieldArgs): ResolvedField {
  const { target, entry, raw, status, effectivePtOptionId } = args;
  const label = target.label;
  const blank = isBlank(raw);

  switch (target.kind) {
    case 'container': {
      const container = target.container!;
      const isProductType = container.container_key === 'product_type';

      // Product Type is load-bearing: a blank cell never clears it, and on a
      // new row it's applied by create (batchAdd), not by an update edit.
      if (isProductType) {
        if (blank) return { field: target.field, label, edits: [], newDisplay: '' };
        const option = resolveContainerOption(container, cellString(raw));
        if (!option) {
          return {
            field: target.field,
            label,
            edits: [],
            newDisplay: '',
            error: `${label}: "${cellString(raw)}" is not a valid option.`,
          };
        }
        const edits = status === 'new' ? [] : [{ field: target.field, value: option.option_code }];
        return { field: target.field, label, edits, newDisplay: option.option_name };
      }

      if (blank)
        return {
          field: target.field,
          label,
          edits: [{ field: target.field, value: null }],
          newDisplay: '',
        };
      const option = resolveContainerOption(container, cellString(raw));
      if (!option) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: "${cellString(raw)}" is not a valid option.`,
        };
      }
      if (!isContainerApplicable(container, effectivePtOptionId)) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: not applicable for the selected Product Type.`,
        };
      }
      // ENG-677: the specific option must also be selectable (green) for the
      // row's Product Type — mirrors the planner dropdown filter. Skip when the
      // PT is unknown (matches isContainerApplicable's null handling).
      if (effectivePtOptionId && !optionAppliesToProductType(option, effectivePtOptionId)) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: "${option.option_name}" is not available for the selected Product Type.`,
        };
      }
      return {
        field: target.field,
        label,
        edits: [{ field: target.field, value: option.option_code }],
        newDisplay: option.option_name,
      };
    }

    case 'number_with_unit': {
      const unitField = target.unitField!;
      if (blank) {
        return {
          field: target.field,
          label,
          edits: [
            { field: target.field, value: null },
            { field: unitField, value: null },
          ],
          newDisplay: '',
        };
      }
      const unit = entry.unit ?? target.defaultUnit ?? '';
      const commit = parseNumberWithUnitPaste(raw, null, unit);
      if (commit === undefined) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: "${cellString(raw)}" is not a valid number.`,
        };
      }
      if (commit === null) {
        return {
          field: target.field,
          label,
          edits: [
            { field: target.field, value: null },
            { field: unitField, value: null },
          ],
          newDisplay: '',
        };
      }
      return {
        field: target.field,
        label,
        edits: [
          { field: target.field, value: commit.value },
          { field: unitField, value: commit.unit },
        ],
        newDisplay: formatNumberWithUnit(commit.value, commit.unit),
      };
    }

    case 'integer': {
      if (blank)
        return {
          field: target.field,
          label,
          edits: [{ field: target.field, value: null }],
          newDisplay: '',
        };
      const n = Number(cellString(raw));
      if (!Number.isInteger(n)) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: "${cellString(raw)}" is not a whole number.`,
        };
      }
      return {
        field: target.field,
        label,
        edits: [{ field: target.field, value: String(n) }],
        newDisplay: String(n),
      };
    }

    case 'date': {
      if (blank)
        return {
          field: target.field,
          label,
          edits: [{ field: target.field, value: null }],
          newDisplay: '',
        };
      const iso = toIsoDate(raw);
      if (iso === undefined) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: "${cellString(raw)}" is not a valid date.`,
        };
      }
      return {
        field: target.field,
        label,
        edits: [{ field: target.field, value: iso }],
        newDisplay: iso,
      };
    }

    case 'metadata':
      return resolveMetadataField(target, raw, blank, label);

    case 'text':
    default: {
      if (blank)
        return {
          field: target.field,
          label,
          edits: [{ field: target.field, value: null }],
          newDisplay: '',
        };
      const text = cellString(raw);
      return {
        field: target.field,
        label,
        edits: [{ field: target.field, value: text }],
        newDisplay: text,
      };
    }
  }
}

function resolveMetadataField(
  target: ImportTarget,
  raw: unknown,
  blank: boolean,
  label: string,
): ResolvedField {
  const clear = (): ResolvedField => ({
    field: target.field,
    label,
    edits: [{ field: target.field, value: null }],
    newDisplay: '',
  });
  const ok = (value: string): ResolvedField => ({
    field: target.field,
    label,
    edits: [{ field: target.field, value }],
    newDisplay: value,
  });
  const bad = (): ResolvedField => ({
    field: target.field,
    label,
    edits: [],
    newDisplay: '',
    error: `${label}: "${cellString(raw)}" is not valid.`,
  });

  switch (target.metadataType) {
    case MT.Number: {
      if (blank) return clear();
      const n = Number(cellString(raw));
      return Number.isFinite(n) ? ok(String(n)) : bad();
    }
    case MT.Date: {
      if (blank) return clear();
      const iso = toIsoDate(raw);
      return iso === undefined ? bad() : ok(iso);
    }
    case MT.DateTime: {
      if (blank) return clear();
      const iso = toIsoDateTime(raw);
      return iso === undefined ? bad() : ok(iso);
    }
    case MT.CheckBox: {
      if (blank) return clear();
      const bool = parseBoolCell(raw);
      return bool === undefined ? bad() : ok(bool);
    }
    case MT.SelectOne: {
      if (blank) return clear();
      const match = matchSelectOption(target.selectOptions ?? [], cellString(raw));
      if (match === undefined) {
        return {
          field: target.field,
          label,
          edits: [],
          newDisplay: '',
          error: `${label}: "${cellString(raw)}" is not an allowed option.`,
        };
      }
      return ok(match);
    }
    case MT.String:
    default: {
      if (blank) return clear();
      return ok(cellString(raw));
    }
  }
}

// ── Display helpers ──────────────────────────────────────────────

/** Current display of a field on an existing row, matching the mapped target's kind. */
function currentDisplay(
  field: string,
  row: WorkOrderRow,
  catalogByField: Map<string, ImportTarget>,
): string {
  // Direct product bind — not a catalog column, so compare the bound product id
  // itself. Without this the diff would read every re-import as a change.
  if (field === PRODUCT_ID_FIELD) return safeString(row.product_id);

  const target = catalogByField.get(field);
  if (!target) return '';
  switch (target.kind) {
    case 'container': {
      const code = asCode(row[field]);
      if (!code) return '';
      const opt = target.container?.options.find((o) => o.option_code === code);
      return opt?.option_name ?? code;
    }
    case 'number_with_unit': {
      const value = row[field] as string | number | null | undefined;
      const unit = target.unitField
        ? ((row[target.unitField] as string | null | undefined) ?? null)
        : null;
      return formatNumberWithUnit(value ?? null, unit);
    }
    case 'metadata': {
      const key = field.slice('metadata.'.length);
      return safeString(row.metadata?.[key]);
    }
    default:
      return safeString(row[field]);
  }
}

/** Build display-only cells for an invalid row (no edits applied). */
function buildInvalidCells(
  resolvedFields: ResolvedField[],
  existingRow: WorkOrderRow | null,
  catalogByField: Map<string, ImportTarget>,
): PreviewCell[] {
  return resolvedFields.map((rf) => {
    const oldDisplay = existingRow ? currentDisplay(rf.field, existingRow, catalogByField) : null;
    return {
      field: rf.field,
      label: rf.label,
      newDisplay: rf.newDisplay,
      oldDisplay,
      changed: existingRow ? oldDisplay !== rf.newDisplay : true,
      locked: false,
    };
  });
}

// ── Value helpers ────────────────────────────────────────────────

/** Safe coercion of an unknown value to a string — objects/arrays become ''. */
function safeString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function isBlank(raw: unknown): boolean {
  return raw === null || raw === undefined || safeString(raw).trim() === '';
}

/** Trimmed string form of an Excel cell (numbers/dates coerced sensibly). */
function cellString(raw: unknown): string {
  if (raw instanceof Date) return toIsoDate(raw) ?? raw.toISOString();
  return safeString(raw).trim();
}

function asCode(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Match an Excel value to a container option by option_code or option_name (case-insensitive). */
export function resolveContainerOption(
  container: PartnerContainer,
  value: string,
): PartnerContainerOption | undefined {
  const needle = value.trim().toLowerCase();
  if (needle === '') return undefined;
  // Only selectable (non-archived) options can be imported.
  const selectable = container.options.filter((o) => o.archived_at == null);
  return (
    selectable.find((o) => o.option_code.toLowerCase() === needle) ??
    selectable.find((o) => o.option_name.toLowerCase() === needle)
  );
}

/** Whether a container applies to the (effective) Product Type, mirroring cell-applicability. */
function isContainerApplicable(container: PartnerContainer, ptOptionId: string | null): boolean {
  if (container.container_key === 'product_type') return true;
  if (!ptOptionId) return true; // unknown PT → don't gate (matches the grid)
  return container.product_type_option_ids.includes(ptOptionId);
}

function matchSelectOption(options: readonly string[], value: string): string | undefined {
  const needle = value.trim().toLowerCase();
  return options.find((o) => o.toLowerCase() === needle);
}

function parseBoolCell(raw: unknown): 'true' | 'false' | undefined {
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  const s = String(raw).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(s)) return 'true';
  if (['false', '0', 'no', 'n'].includes(s)) return 'false';
  return undefined;
}

/** Coerce an Excel date cell (Date object, YYYY-MM-DD, or parseable string) to YYYY-MM-DD. */
export function toIsoDate(raw: unknown): string | undefined {
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return undefined;
    return formatLocalDate(raw);
  }
  const s = String(raw).trim();
  if (s === '') return undefined;
  // Already ISO date or datetime — validate the calendar date, then take the
  // date part verbatim (no tz shift). Rejects e.g. 2026-13-99 / 2026-02-30.
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const dt = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    const valid =
      dt.getUTCFullYear() === Number(year) &&
      dt.getUTCMonth() === Number(month) - 1 &&
      dt.getUTCDate() === Number(day);
    return valid ? `${year}-${month}-${day}` : undefined;
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return formatLocalDate(parsed);
}

/** Coerce an Excel datetime cell to an ISO 8601 string. */
export function toIsoDateTime(raw: unknown): string | undefined {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? undefined : raw.toISOString();
  }
  const s = String(raw).trim();
  if (s === '') return undefined;
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
